import { NodeSelection } from 'prosemirror-state'
import { nextTick } from 'vue'
import VueRenderer from './VueRenderer'

class VueNodeView {
  constructor (component, props, options) {
    this.options = { ...this.options, ...options }
    this.editor = props.editor
    this.extension = props.extension
    this.node = props.node
    this.getPos = props.getPos
    this.mount(component)
  }

  handleDragStart (event) {
    const { view } = this.editor
    const target = event.target

    if (this.contentDOM?.contains(target)) {
      return
    }

    // sometimes `event.target` is not the `dom` element
    /* eslint-disable-next-line */
    event.dataTransfer?.setDragImage(this.dom, 0, 0)

    const selection = NodeSelection.create(view.state.doc, this.getPos())
    const transaction = view.state.tr.setSelection(selection)

    view.dispatch(transaction)
  }

  mount (component) {
    const { handleDragStart } = this
    const dragstart = handleDragStart.bind(this)

    const props = {
      editor: this.editor,
      node: this.node,
      decorations: this.decorations,
      selected: false,
      extension: this.extension,
      getPos: () => this.getPos(),
      updateAttributes: (attributes, pos) => this.updateAttributes(attributes, pos),
      contentAs: this.options.contentAs
    }

    const rendererOptions = {
      as: this.options.as,
      props: {
        ...props
      }
    }
    if (this.options.nodeRendererName) {
      rendererOptions.nodeRendererName = this.options.nodeRendererName
    }
    this.renderer = new VueRenderer(component, rendererOptions)

    this.renderer.element.addEventListener('dragstart', (ev) => dragstart(ev));
    (this.renderer.element).style.whiteSpace = 'normal'

    if (this.options.hasContent) {
      this.$contentDOM = document.createElement(this.options.contentAs)
      this.dom.appendChild(this.$contentDOM)

      nextTick(() => {
        const el = this.dom.querySelector('[data-NodeViewContent]')
        if (!el) {
          throw Error('No slot for node new content found.')
        }
        if (el && !this.$doneContentDOM) {
          this.$doneContentDOM = true
          el.appendChild(this.$contentDOM)
        }
      })
    }
  }

  get dom () {
    return this.renderer.element
  }

  get contentDOM () {
    return this.$contentDOM
  }

  stopEvent (event) {
    if (typeof this.options.stopEvent === 'function') {
      return this.options.stopEvent(event)
    }

    const target = (event.target)
    const isInElement = this.dom.contains(target) && !this.contentDOM?.contains(target)

    // ignore all events from child nodes
    if (!isInElement) {
      return false
    }

    const { isEditable } = this.editor
    const isDraggable = this.node.type.spec.draggable
    const isCopyEvent = event.type === 'copy'
    const isPasteEvent = event.type === 'paste'
    const isCutEvent = event.type === 'cut'
    const isDragEvent = event.type.startsWith('drag') || event.type === 'drop'

    if (isDraggable && isDragEvent && !this.isDragging) {
      event.preventDefault()
      return false
    }

    // we have to store that dragging started
    if (isDraggable && isEditable && !this.isDragging && event.type === 'mousedown') {
      const dragHandle = target.closest('[data-drag-handle]')
      const isValidDragHandle = dragHandle &&
        (this.dom === dragHandle || this.dom.contains(dragHandle))

      if (isValidDragHandle) {
        this.isDragging = true
        document.addEventListener('dragend', () => {
          this.isDragging = false
        }, { once: true })
      }
    }

    // these events are handled by prosemirror
    if (this.isDragging || isCopyEvent || isPasteEvent || isCutEvent) {
      return false
    }

    return true
  }

  ignoreMutation (mutation) {
    if (mutation.type === 'selection') {
      if (this.node.isLeaf) {
        return true
      }

      return false
    }

    if (!this.contentDOM) {
      return true
    }

    const contentDOMHasChanged = !this.contentDOM.contains(mutation.target) ||
      this.contentDOM === mutation.target

    return contentDOMHasChanged
  }

  destroy () {
    if (this.$contentDOM) {
      this.$contentDOM.remove()
    }
    this.renderer.destroy()
  }

  update (node, decorations) {
    if (typeof this.options.update === 'function') {
      return this.options.update(node, decorations)
    }

    if (node.type !== this.node.type) {
      return false
    }

    if (node === this.node && this.decorations === decorations) {
      return true
    }

    this.node = node
    this.decorations = decorations
    this.renderer.updateProps({ node, decorations })

    return true
  }

  updateAttributes (attributes, $pos) {
    if (!this.editor.view.editable) {
      return
    }

    const { state, dispatch } = this.editor.view
    const pos = $pos || this.getPos()
    const transaction = state.tr.setNodeMarkup(pos, undefined, {
      ...this.node.attrs,
      ...attributes
    })

    dispatch(transaction)
  }

  selectNode () {
    this.renderer.updateProps({
      selected: true
    })
  }

  deselectNode () {
    this.renderer.updateProps({
      selected: false
    })
  }
}

export default function VueNodeViewRenderer (component, options) {
  return (props) => {
    return new VueNodeView(component, props, options)
  }
}
